import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  kycAgentCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { kycEndpoints } from "@/features/kyc/api/endpoints";
import type { AgentDto } from "@/features/kyc/api/dto";

/**
 * Enrôle un agent (`POST /Agents/add`). Idempotency-Key NON requis sur cet
 * endpoint (guide §6.2 — contrairement à Parties/add). `agentType` vaut
 * BOTH par défaut côté serveur si omis.
 */
export const useCreateAgent = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["kyc", "agents", "create"],
    mutationFn: (body: AgentDto) =>
      mutateData({
        url: kycEndpoints.agents.add,
        reqBody: body as unknown as Record<string, unknown>,
        method: "POST",
      }) as Promise<AgentDto>,
    onSuccess: () => {
      return invalidateAfterMutation(queryClient, kycAgentCacheKeys(), {
        resetListPage: true,
      });
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useCreateAgent failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "kyc" }
      );
    },
  });
};
